import { cn } from '#app/utils/misc'

export interface AccordionProps {
	eyebrow?: string
	title?: string
	description?: string
	background?: 'default' | 'muted' | 'primary-light'
	items: {
		eyebrow?: string
		question: string
		answer: string
		detail?: string
	}[]
}

const backgroundStyles = {
	default: 'bg-background',
	muted: 'bg-transparent',
	'primary-light': 'bg-primary/5',
}

export function Accordion({
	eyebrow,
	title,
	description,
	background = 'muted',
	items,
}: AccordionProps) {
	const hasHeader = Boolean(title || description)

	return (
		<section
			className={cn(
				'px-6 py-24 sm:px-8 lg:px-12',
				backgroundStyles[background],
			)}
		>
			<div
				className={`mx-auto grid ${
					hasHeader
						? 'max-w-6xl gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'
						: 'max-w-3xl gap-6'
				}`}
			>
				{hasHeader && (
					<div
						className={cn(
							'space-y-3',
							!description && 'mb-10 text-center md:col-span-full',
						)}
					>
						{eyebrow && (
							<p className="text-label text-muted-foreground">{eyebrow}</p>
						)}
						{title && <h2 className="text-title-2">{title}</h2>}
						{description && (
							<p className="text-muted-foreground text-base md:text-lg">
								{description}
							</p>
						)}
					</div>
				)}
				<div className="divide-primary/8 border-primary/8 divide-y border-y">
					{items.map((item, index) => (
						<details key={index} className="group py-5">
							<summary className="flex cursor-pointer list-none items-center justify-between text-left">
								<div className="space-y-2">
									{item.eyebrow ? (
										<p className="text-label text-primary">{item.eyebrow}</p>
									) : null}
									<span className="text-foreground group-open:text-primary block text-base font-medium transition-colors">
										{item.question}
									</span>
								</div>
								<span className="text-muted-foreground ml-4 shrink-0 transition-transform group-open:rotate-45">
									+
								</span>
							</summary>
							<div className="mt-3 space-y-3 pr-8">
								<p className="text-muted-foreground text-sm leading-relaxed">
									{item.answer}
								</p>
								{item.detail ? (
									<p className="text-muted-foreground text-sm leading-6 font-medium">
										{item.detail}
									</p>
								) : null}
							</div>
						</details>
					))}
				</div>
			</div>
		</section>
	)
}
